import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Field } from 'redux-form'
import Input from 'react-phone-number-input/input'
import en from 'react-phone-number-input/locale/en.json'
import { getCountries, getCountryCallingCode } from 'react-phone-number-input'
import { isValidNumber, parsePhoneNumber } from 'libphonenumber-js'

import { useGetUserCountryAndStripeKeyQuery } from 'hooks/useQueries'

import ChevronDown from '../icons/ChevronDown2'
import ValidIcon from '../icons/Valid'

import { InputGroup, SelectField } from '.'

export const validPhoneNumber = (value) => {
  if (value && !isValidNumber(value)) {
    return 'Not a valid phone number'
  }

  return undefined
}

const PhoneInputWrapper = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  overflow: hidden;

  margin: 8px 0 8px;

  border: 1px solid
    ${({ theme, error, success }) => {
      if (error) {
        return theme?.['-ds-error-300']?.value
      }

      if (success) {
        return theme.brand.jungle
      }

      return theme.colors.textGreyLight
    }};

  ${({ rounded }) =>
    rounded &&
    `
    border-radius: 6px;
  `}

  input {
    width: 100%;
    padding: 18px;
    border: none;
    outline: none;
  }

  .PhoneInputCountry {
    padding-left: 18px;
  }

  & > svg {
    position: absolute;

    top: 50%;
    width: 20px;

    height: 20px;
    right: 20px;

    color: #9b9b9b;

    transform: translateY(-50%);
  }
`

const CountrySelect = ({ value, onChange, labels = en, rounded, ...rest }) => {
  const countries = getCountries()

  const preferredCountries = ['AU', 'NZ', 'US']

  return (
    <SelectField className="select-field" white rounded={rounded}>
      &nbsp;
      <select
        {...rest}
        value={value}
        onChange={(event) => onChange(event.target.value || undefined)}
        aria-label="Select country"
      >
        <option value={undefined} disabled>
          Select country code
        </option>
        <option
          style={{ fontSize: '1pt', backgroundColor: '#000000' }}
          disabled
        >
          &nbsp;
        </option>
        {preferredCountries.map((country) => (
          <option key={country} value={country}>
            {labels[country]} +{getCountryCallingCode(country)}
          </option>
        ))}
        <option
          style={{ fontSize: '1pt', backgroundColor: '#000000' }}
          disabled
        >
          &nbsp;
        </option>
        {countries.map((country) => (
          <option key={country} value={country}>
            {labels[country]} +{getCountryCallingCode(country)}
          </option>
        ))}
      </select>
      <span>
        <ChevronDown style={{ width: '20px', height: 'auto' }} />
      </span>
    </SelectField>
  )
}

const useCountryState = (value) => {
  const [country, setCountry] = useState()

  const { data } = useGetUserCountryAndStripeKeyQuery({
    fetchPolicy: 'cache-and-network',
    skip: country != null,
  })

  const userDefaultCountryCode = data?.currentUser?.defaultCountry?.code

  const setOnceFromValue = useRef(false)

  // set initial state once based on on user's default country unless it's been set based on initial value
  useEffect(() => {
    if (country == null && setOnceFromValue.current === false) {
      setCountry(userDefaultCountryCode)
    }
  }, [userDefaultCountryCode, setOnceFromValue])

  // set initial state once based on value -- this pre-populates the field when a saved phone number already exists
  useEffect(() => {
    if (setOnceFromValue.current === true) {
      return
    }

    let parsed

    try {
      parsed = parsePhoneNumber(value)
    } catch (parseError) {
      if (parseError?.message === 'TOO_SHORT') {
        // ignore, this is okay, as parsed object will still have country property with a value
      }
    }

    if (parsed?.country) {
      setCountry(parsed?.country)
      setOnceFromValue.current = true
    }
  }, [value, setOnceFromValue])

  return [country, setCountry]
}

export const PhoneInput = ({
  input,
  meta: { error, submitFailed },
  showAsValid,
  phoneInputClassName,
  rounded,
  ...custom
}) => {
  const { value, onChange, name: phoneInputName } = input

  const [country, setCountry] = useCountryState(value)

  const handleChangeCountry = (newCountry) => {
    onChange('') // reset phone number value when a new country is chosen
    setTimeout(() => {
      setCountry(newCountry)
    }, 0)
  }

  return (
    <InputGroup noMargin>
      <CountrySelect
        value={country}
        onChange={handleChangeCountry}
        rounded={rounded}
      />
      <PhoneInputWrapper rounded={rounded}>
        <Input
          country={country}
          // react-phone-number-input/input expects full number as value (inc. country calling code) and
          // when given a country prop, will format this full number in that country's national format
          value={value}
          onChange={onChange}
          error={error && submitFailed}
          name={phoneInputName}
          {...custom}
          aria-label="Phone number"
          disabled={!country}
        />
        {showAsValid && <ValidIcon data-testid="phone_number_valid_icon" />}
      </PhoneInputWrapper>
    </InputGroup>
  )
}

export default (props) => {
  return (
    <Field
      component={PhoneInput}
      normalize={(value) => (value === undefined ? '' : value)}
      {...props}
    />
  )
}
